import styled, { css } from 'styled-components';

import { designColor, floorGameDuration as duration, floorGameEasing as easing, styledReset } from '@pkg/primitives';

import { tokens } from './DataTable.tokens';

interface StyledHeadHeaderProps {
    $sortOffset?: boolean;
}

// NOTE: This component has several complex style solutions.
// See these CodePen demoes for more insight:
// Rounded corner masking: https://codepen.io/beefchimi/pen/vYQrEde
// Sticky table elements: https://codepen.io/beefchimi/pen/poQVWWN

// prettier-ignore
const maskStyles = css`
    mask-image:
        // Slim rect to cover the <thead />
        linear-gradient(
            to bottom,
            black var(--data-table-thead-height),
            transparent var(--data-table-thead-height)
        ),
        // Large rect to cover top-to-bottom (inset from left/right)
        linear-gradient(
            to bottom,
            transparent var(--mask-vert-rect-start),
            black var(--mask-vert-rect-start),
            black calc(100% - var(--mask-radius)),
            transparent calc(100% - var(--mask-radius))
        ),
        // Large rect to cover left-to-right (inset from top/bottom)
        linear-gradient(
            to right,
            transparent var(--mask-radius),
            black var(--mask-radius),
            black calc(100% - var(--mask-radius)),
            transparent calc(100% - var(--mask-radius))
        ),
        // Circles for each corner
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur)),
        radial-gradient(circle, black var(--mask-radius), transparent var(--mask-radius-blur));
    mask-size:
        cover,
        cover,
        cover,
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size),
        var(--mask-circle-size) var(--mask-circle-size);
    mask-position:
        center,
        center,
        center var(--data-table-thead-height),
        left var(--data-table-thead-height),
        right var(--data-table-thead-height),
        bottom right,
        bottom left;
    mask-repeat: no-repeat;
`;

export const StyledDataTable = styled.div`
    ${styledReset.borderBox};

    --data-table-cell-padding: ${tokens.cell.padding}px;
    --data-table-cell-padding-small: calc(var(--data-table-cell-padding) - ${tokens.sortAction.spacing}px);
    --data-table-cell-padding-big: calc(var(--data-table-cell-padding) + ${tokens.sortAction.spacing}px);

    --data-table-thead-height: ${tokens.thead.height}px;
    --data-table-bg-color: ${designColor.raisin};
    --data-table-highlight-color: ${designColor.raisinLight};

    --mask-radius: ${tokens.table.radius}px;
    --mask-radius-blur: ${tokens.table.radius + 1}px;
    --mask-circle-size: calc(var(--mask-radius-blur) * 2);
    --mask-vert-rect-start: calc(var(--data-table-thead-height) + var(--mask-radius));

    &[data-table-align='start'] {
        --data-table-head-header-padding-left: var(--data-table-cell-padding-small);
        --data-table-head-header-padding-right: var(--data-table-cell-padding);
    }

    &[data-table-align='end'] {
        --data-table-head-header-padding-left: var(--data-table-cell-padding);
        --data-table-head-header-padding-right: var(--data-table-cell-padding-small);
    }

    &[data-table-collapse='collapse'] {
        --data-table-border-collapse: collapse;
        ${maskStyles};
        overflow: auto;

        // We might find 'proximity' to feel better.
        scroll-snap-type: x mandatory;

        // Unfortunately, we cannot use "start align" snapping
        // because the first "sticky cell" width is dynamic.
        // This also means we cannot prescribe a left-offset...
        // however, we could reasonably predict this for a
        // "truncated Collection" variant:
        /*
        scroll-padding-left:
            calc(
                var(--data-table-cell-padding) +
                var(--data-table-cell-padding-big) +
                // tokens.collection.minWidth
                144px
            );
        */
    }

    &[data-table-collapse='separate'] {
        --data-table-border-collapse: separate;

        --data-table-cell-radius-top-left: ${tokens.table.radius}px;
        --data-table-cell-radius-bottom-left: ${tokens.table.radius}px;
        --data-table-cell-radius-top-right: ${tokens.table.radius}px;
        --data-table-cell-radius-bottom-right: ${tokens.table.radius}px;
    }
`;

export const Table = styled.table`
    ${styledReset.table};
    border-collapse: var(--data-table-border-collapse);
    border-spacing: 0 ${tokens.row.gap}px;
`;

export const TableRow = styled.tr`
    background-color: transparent;
`;

const cellBase = css`
    ${styledReset.tableCell};
    padding: var(--data-table-cell-padding);
    white-space: nowrap;

    ${StyledDataTable}[data-table-align='end'] &:last-child {
        --data-table-last-cell-align: end;
        text-align: right;
    }

    ${StyledDataTable}[data-table-collapse='collapse'] & {
        // "end" is required so that we snap to the right-edge of the
        // scroll container. Since we have sticky elements that are dynamic,
        // we cannot use 'scroll-padding' to predict an offset.
        scroll-snap-align: end;

        &:first-child {
            padding-right: var(--data-table-cell-padding-big);
        }
    }
`;

///
/// Table Head

export const TableHead = styled.thead``;

export const TableHeadHeader = styled.th<StyledHeadHeaderProps>`
    ${cellBase};
    height: var(--data-table-thead-height);

    ${({ $sortOffset }) =>
        $sortOffset &&
        css`
            padding-left: var(--data-table-cell-padding-small);

            &:last-child {
                padding-left: var(--data-table-head-header-padding-left);
                padding-right: var(--data-table-head-header-padding-right);
            }
        `};

    ${StyledDataTable}[data-table-collapse='collapse'] & {
        // TODO: Inorder for "sticky headers" to sticky to the top,
        // the <table /> needs to also be vertically scrollable.
        // We prefer to have the page be scrollable... so stickyness
        // will not work at the moment.
        z-index: 1;
        position: sticky;
        top: 0;

        background-color: ${designColor.raisinDark};
    }

    ${StyledDataTable}[data-table-collapse='collapse'] &:first-child {
        z-index: 2;
        left: 0;

        background: linear-gradient(
            to right,
            ${designColor.raisinDark} calc(100% - var(--data-table-cell-padding-big)),
            transparent
        );
    }
`;

export const TableHeadHeaderInterior = styled.div`
    display: grid;
    justify-content: var(--data-table-last-cell-align, start);
`;

///
/// Table Body

export const TableBody = styled.tbody`
    ${StyledDataTable}[data-table-collapse='collapse'] & {
        background-color: var(--data-table-bg-color);

        ${TableRow} {
            transition: background-color ${duration.normal}ms ${easing.cubic};

            &:hover {
                background-color: var(--data-table-highlight-color);
            }
        }
    }
`;

const bodyCellBase = css`
    ${cellBase};
    justify-content: var(--data-table-last-cell-align);

    ${StyledDataTable}[data-table-collapse='separate'] & {
        transition: background-color ${duration.normal}ms ${easing.cubic};
        background-color: var(--data-table-bg-color);
    }

    ${StyledDataTable}[data-table-collapse='separate'] ${TableBody} ${TableRow}:hover & {
        background-color: var(--data-table-highlight-color);
    }
`;

const firstCellCorners = css`
    border-top-left-radius: var(--data-table-cell-radius-top-left);
    border-bottom-left-radius: var(--data-table-cell-radius-bottom-left);
`;

export const TableBodyHeader = styled.th`
    ${bodyCellBase};

    ${StyledDataTable}[data-table-collapse='collapse'] & {
        z-index: 2;
        position: sticky;
        left: 0;

        // Background with "right-feather" has to be a psuedo-element
        // with a 'mask' in order for us to use 'transition' in Safari.
        &::before {
            content: '';
            display: block;
            position: absolute;
            inset: 0;
            background-color: var(--data-table-bg-color);
            transition: background-color ${duration.normal}ms ${easing.cubic};
            // prettier-ignore
            mask: linear-gradient(
                to right,
                black calc(100% - var(--data-table-cell-padding-big)),
                transparent
            );
        }
    }

    ${StyledDataTable}[data-table-collapse='collapse'] ${TableRow}:hover &::before {
        background-color: var(--data-table-highlight-color);
    }

    ${StyledDataTable}[data-table-collapse='separate'] & {
        ${firstCellCorners};
    }
`;

export const TableBodyCell = styled.td`
    ${bodyCellBase};

    ${StyledDataTable}[data-table-collapse='separate'] ${TableBody} &:first-child {
        ${firstCellCorners};
    }

    ${StyledDataTable}[data-table-collapse='separate'] ${TableBody} &:last-child {
        border-top-right-radius: var(--data-table-cell-radius-top-right);
        border-bottom-right-radius: var(--data-table-cell-radius-bottom-right);
    }
`;
